import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { useIntegrations } from '../lib/IntegrationContext';
import { useArticles, useBrandOnboarded, useSites, useWorkspace, qk } from '../lib/queries';
import { runDeleteBatch, formatBatchSummary } from '../lib/deleteOutcomes';
import Icon from '../components/Icon';
import Dialog from '../components/Dialog';
import { StatusBadge, StatePanel, SkeletonRows, PageHead, Spinner, MenuButton, useMediaQuery, NARROW } from '../components/ui';

// Destination registry — add new publish targets here as they come online
const DESTINATIONS = [
  { id: 'freshdesk',  label: 'Freshdesk',  idField: 'freshdesk_id' },
  { id: 'confluence', label: 'Confluence', idField: 'confluence_page_id' },
  // { id: 'notion',  label: 'Notion',     idField: 'notion_page_id' },
];

// Backend statuses → display. Labels map 1:1; draft / synced ("pushed") /
// published are deliberately different tones and never collapsed.
const STATUS = {
  draft:     { label: 'Draft',     tone: 'neutral' },
  pushed:    { label: 'Synced',    tone: 'info' },
  published: { label: 'Published', tone: 'success' },
};

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function Dashboard({ onEdit, onUpdate, onNew }) {
  const { status: integrationStatus } = useIntegrations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Articles + onboarding flag — both cached for 30s, instant on re-nav
  const { data: articles = [], isLoading: loading, isError: articlesError, isFetching: articlesFetching, refetch: refetchArticles } = useArticles();
  const { data: brandOnboardedFromServer = true } = useBrandOnboarded();

  // GP-23 — Destinations section (ported from the old Publish page). Uses the
  // same hooks + copy + persona hiding as Publish.js. Kept collapsed by default
  // so it stays visually secondary to the article list.
  const { data: hostedSites = [] } = useSites();
  const { data: workspace } = useWorkspace();
  const isDesignPersona = workspace?.use_case === 'design';
  const freshdeskConnected = integrationStatus.freshdesk;
  const atlassianConnected = integrationStatus.atlassian;

  // Route into the Editor's publish flow rather than duplicating per-destination
  // publish logic here. The state hint is harmless if the editor ignores it.
  const publishArticle = (id) => navigate(`/editor/${id}`, { state: { openPublish: true } });

  const goToSites = () => navigate('/settings#sites');
  const addSite = () => navigate('/settings?new-site=1#sites');
  const goToConnections = (category) => {
    const cat = category ? `?category=${category}` : '';
    navigate(`/settings${cat}#connections`);
  };
  const activeSitesCount = hostedSites.filter(s => s.status !== 'paused').length;

  const list = Array.isArray(articles) ? articles : [];
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch modal state
  const [showFetchModal, setShowFetchModal]       = useState(false);
  const [fetchingFD, setFetchingFD]               = useState(false);
  const [fetchingConf, setFetchingConf]           = useState(false);
  const [fetchResult, setFetchResult]             = useState(null);
  const [fetchError, setFetchError]               = useState('');
  const [confSpaces, setConfSpaces]               = useState([]);
  const [confSites, setConfSites]                 = useState([]);
  const [selectedSpace, setSelectedSpace]         = useState('');
  const [loadingConfSpaces, setLoadingConfSpaces] = useState(false);

  // Local "dismissed this session" state — overrides the server flag without a round-trip
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBrandBanner = !brandOnboardedFromServer && !bannerDismissed;

  const dismissBrandBanner = () => {
    setBannerDismissed(true);
    api.put('/brand/onboarded', { onboarded: true })
      .then(() => queryClient.invalidateQueries({ queryKey: qk.brandOnboarded() }))
      .catch(() => {});
  };

  const loadArticles = () => refetchArticles();

  const [deleteErrors, setDeleteErrors] = useState({});
  // F01 — persistent batch summary: {text, failedIds, clean}|null
  const [deleteSummary, setDeleteSummary] = useState(null);

  // GP-10 — bulk multi-select on the dashboard list
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setSelectMode(false); };

  // Optimistically remove the deleted article from the cached list, then
  // invalidate so background refetch confirms.
  const removeArticleFromCache = (id) => {
    queryClient.setQueryData(qk.articles(), (prev) =>
      Array.isArray(prev) ? prev.filter(x => x.id !== id) : prev
    );
  };

  // F01 — a row leaves the list only after the server confirms the local
  // deletion. Local deletion and remote cleanup succeed independently, so a
  // failed remote cleanup never reads as "also removed from …".
  const applyDeleteOutcomes = (outcomes) => {
    outcomes.filter(o => o.localDeleted).forEach(o => removeArticleFromCache(o.id));
    setDeleteErrors(prev => {
      const next = { ...prev };
      for (const o of outcomes) {
        if (o.localDeleted) delete next[o.id];
        else next[o.id] = o.error;
      }
      return next;
    });
  };

  const runDeletes = async (ids, { fromSelection = false } = {}) => {
    if (!ids.length) return null;
    const { outcomes, summary } = await runDeleteBatch(ids, async (id) => {
      const r = await api.delete(`/articles/${id}`);
      return r.data;
    });
    applyDeleteOutcomes(outcomes);

    const clean = summary.failed === 0 && summary.remoteFailed.length === 0;
    setDeleteSummary({ text: formatBatchSummary(summary), failedIds: summary.failedIds, clean });
    // A clean result can fade; anything that failed stays until dismissed.
    if (clean) setTimeout(() => setDeleteSummary(null), 4000);

    if (fromSelection) {
      // Retain exactly the failed rows so a retry targets only failed work.
      if (summary.failedIds.length) setSelectedIds(new Set(summary.failedIds));
      else clearSelection();
    }
    // Confirm against the server regardless of outcome.
    queryClient.invalidateQueries({ queryKey: qk.articles() });
    return summary;
  };

  const handleDelete = async (id) => { await runDeletes([id]); };

  // GP-10 — bulk delete selected docs (sequential to surface per-doc errors)
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try { await runDeletes([...selectedIds], { fromSelection: true }); }
    finally { setBulkDeleting(false); }
  };

  const handleRetryFailedDeletes = async () => {
    const ids = deleteSummary?.failedIds || [];
    if (!ids.length) return;
    setBulkDeleting(true);
    try { await runDeletes(ids, { fromSelection: true }); }
    finally { setBulkDeleting(false); }
  };

  // Bulk re-push every Freshdesk-synced doc — propagates updated GIFs/content to
  // the help center, preserving each doc's live/draft state. Reuses the Editor's
  // tested publish-freshdesk endpoint, one at a time from the browser.
  const [republish, setRepublish] = useState(null); // {running,total,done,ok,failed:[]}|null
  const republishing = republish?.running;

  const handleRepublishAll = async () => {
    const targets = articles.filter(a => a.freshdesk_id);
    if (!targets.length) return;
    if (!window.confirm(
      `Re-push ${targets.length} Freshdesk doc${targets.length === 1 ? '' : 's'} to update their content + GIFs? ` +
      `Published docs stay live, drafts stay drafts.`
    )) return;
    setRepublish({ running: true, total: targets.length, done: 0, ok: 0, failed: [] });
    for (const a of targets) {
      const action = a.status === 'published' ? 'publish' : 'push';
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/articles/${a.id}/publish-freshdesk`, { action });
        setRepublish(p => ({ ...p, done: p.done + 1, ok: p.ok + 1 }));
      } catch (e) {
        const error = e.response?.data?.error || e.message || 'failed';
        setRepublish(p => ({ ...p, done: p.done + 1, failed: [...p.failed, { id: a.id, title: a.title, error }] }));
      }
    }
    setRepublish(p => ({ ...p, running: false }));
    refetchArticles();
  };

  // GP-09 — change (or clear) a doc's parent
  const handleChangeParent = async (id, parentId) => {
    queryClient.setQueryData(qk.articles(), (prev) =>
      Array.isArray(prev) ? prev.map(x => x.id === id ? { ...x, parent_id: parentId } : x) : prev
    );
    try {
      await api.put(`/articles/${id}`, { parent_id: parentId });
    } finally {
      queryClient.invalidateQueries({ queryKey: qk.articles() });
    }
  };

  const openFetchModal = () => {
    setFetchResult(null);
    setFetchError('');
    setShowFetchModal(true);
    // Only load Confluence spaces if Atlassian is actually connected
    if (integrationStatus.atlassian) {
      setLoadingConfSpaces(true);
      api.get('/confluence/spaces')
        .then(r => {
          setConfSpaces(r.data.spaces || []);
          setConfSites(r.data.sites || []);
          setSelectedSpace(r.data.spaces?.[0]?.key || '');
        })
        .catch(() => {})
        .finally(() => setLoadingConfSpaces(false));
    }
  };

  const handleImportFreshdesk = async () => {
    setFetchingFD(true);
    setFetchError('');
    setFetchResult(null);
    try {
      const r = await api.post('/import/freshdesk');
      setFetchResult({ source: 'Freshdesk', ...r.data });
      loadArticles();
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Freshdesk import failed');
    } finally {
      setFetchingFD(false);
    }
  };

  const handleImportConfluence = async () => {
    if (!selectedSpace) return;
    setFetchingConf(true);
    setFetchError('');
    setFetchResult(null);
    try {
      const cloud_id = confSites[0]?.id;
      if (!cloud_id) { setFetchError('No Confluence site found'); setFetchingConf(false); return; }
      const r = await api.post('/import/confluence', { cloud_id, space_key: selectedSpace });
      setFetchResult({ source: 'Confluence', ...r.data });
      loadArticles();
    } catch (e) {
      setFetchError(e.response?.data?.error || 'Confluence import failed');
    } finally {
      setFetchingConf(false);
    }
  };

  const isNarrow = useMediaQuery(NARROW);

  // ── Filtering (client-side over the loaded list; no pagination implied) ──
  const q = search.trim().toLowerCase();
  const tabs = [
    { id: 'all',       label: 'All',       count: list.filter(a => a.use_case !== 'design_review').length },
    { id: 'draft',     label: 'Draft',     count: list.filter(a => a.status === 'draft' && a.use_case !== 'design_review').length },
    { id: 'pushed',    label: 'Synced',    count: list.filter(a => a.status === 'pushed' && a.use_case !== 'design_review').length },
    { id: 'published', label: 'Published', count: list.filter(a => a.status === 'published' && a.use_case !== 'design_review').length },
    // Review filter is by article use_case, not status — surfaces design_review
    // articles regardless of where they are in the draft/synced/published flow.
    { id: 'review',    label: 'Review',    count: list.filter(a => a.use_case === 'design_review').length },
  ].filter(t => t.id === 'all' || t.count > 0);

  const matchesFilter = a => {
    if (q && !a.title?.toLowerCase().includes(q)) return false;
    if (statusFilter === 'review') return a.use_case === 'design_review';
    if (a.use_case === 'design_review') return false;
    if (statusFilter === 'all') return true;
    return a.status === statusFilter;
  };

  // Parent → children, children listed under their parent.
  const childrenOf = {};
  list.forEach(a => {
    if (a.parent_id) (childrenOf[a.parent_id] = childrenOf[a.parent_id] || []).push(a);
  });
  const parents = list.filter(a => !a.parent_id && matchesFilter(a));
  const rows = [];
  parents.forEach(a => {
    rows.push({ article: a, depth: 0 });
    (childrenOf[a.id] || []).forEach(child => rows.push({ article: child, depth: 1 }));
  });
  // Orphaned children whose parent didn't match the filter
  list.filter(a => a.parent_id && matchesFilter(a) && !parents.find(p => p.id === a.parent_id))
    .forEach(a => rows.push({ article: a, depth: 0 }));

  const onTabKey = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const n = tabs.length;
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    setStatusFilter(tabs[next].id);
    document.getElementById(`pub-tab-${tabs[next].id}`)?.focus();
  };

  const rowProps = ({ article: a, depth }) => ({
    key: a.id,
    article: a,
    depth,
    onEdit: () => onEdit(a.id),
    onUpdate: () => onUpdate(a.id),
    onPublish: () => publishArticle(a.id),
    onDelete: () => handleDelete(a.id),
    deleteError: deleteErrors[a.id],
    parentTitle: a.parent_id ? (list.find(x => x.id === a.parent_id)?.title || '') : '',
    selectMode,
    selected: selectedIds.has(a.id),
    onToggleSelect: () => toggleSelected(a.id),
    allArticles: list,
    onChangeParent: (pid) => handleChangeParent(a.id, pid),
  });

  const hasFreshdeskDocs = articles.some(a => a.freshdesk_id);
  const hasDocs = !loading && list.length > 0;

  return (
    <div className="page">
      {deleteSummary && (
        <div className={`toast ${deleteSummary.clean ? 'is-success' : 'is-error'}`}
             role={deleteSummary.clean ? 'status' : 'alert'}>
          <Icon name={deleteSummary.clean ? 'checkCircle' : 'alert'} />
          <span style={{ flex: 1 }}>{deleteSummary.text}</span>
          {deleteSummary.failedIds.length > 0 && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleRetryFailedDeletes}
              disabled={bulkDeleting}
            >
              Retry {deleteSummary.failedIds.length} failed
            </button>
          )}
          {!deleteSummary.clean && (
            <button type="button" className="icon-btn" aria-label="Dismiss"
                    onClick={() => setDeleteSummary(null)}>
              <Icon name="close" />
            </button>
          )}
        </div>
      )}
      {republish && !republishing && (
        <div className={`toast ${republish.failed.length ? 'is-error' : 'is-success'}`} role={republish.failed.length ? 'alert' : 'status'}>
          <Icon name={republish.failed.length ? 'alert' : 'checkCircle'} />
          <span style={{ flex: 1 }}>
            {republish.failed.length === 0
              ? `Republished ${republish.ok} doc${republish.ok === 1 ? '' : 's'} to Freshdesk`
              : `Republished ${republish.ok}, ${republish.failed.length} failed: ${republish.failed.map(f => `#${f.id} (${f.error})`).join('; ')}`}
          </span>
          <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => setRepublish(null)}>
            <Icon name="close" />
          </button>
        </div>
      )}

      <PageHead
        title="Publish"
        subtitle={loading ? 'Share approved content to the right places.' : `${list.length} doc${list.length !== 1 ? 's' : ''} in your knowledge base`}
        actions={
          <>
            {hasDocs && (
              <div className="search-field" style={{ width: isNarrow ? '100%' : 280 }}>
                <Icon name="search" />
                <label htmlFor="pub-search" className="visually-hidden">Search documents</label>
                <input
                  id="pub-search"
                  className="input"
                  type="search"
                  placeholder="Search documents"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            )}
            <button type="button" className="btn btn-primary" onClick={onNew}>
              <Icon name="plus" /> New document
            </button>
          </>
        }
      />

      {/* Brand-setup nudge */}
      {showBrandBanner && (
        <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderColor: 'var(--gp-accent)', background: 'var(--gp-accent-dim)' }}>
          <Icon name="sparkle" size={24} style={{ color: 'var(--gp-accent-text)', flexShrink: 0 }} />
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--gp-ink)' }}>
              Set your brand once — colours, tone, and writing style.
            </div>
            <div className="section-sub">
              Applied automatically to every article you publish. You can change it anytime.
            </div>
          </div>
          <Link to="/settings#brand" className="btn btn-primary">Set up brand</Link>
          <button type="button" className="icon-btn" onClick={dismissBrandBanner} aria-label="Dismiss brand setup reminder">
            <Icon name="close" />
          </button>
        </div>
      )}

      {/* Destinations summary — configuration status only; publishing an
          individual doc happens from its editor. */}
      <section className="panel dest-strip" aria-labelledby="dest-strip-title">
        <div className="dest-strip-intro">
          <Icon name="book" size={24} />
          <div>
            <h2 id="dest-strip-title" className="section-title">Publishing destinations</h2>
            <p className="section-sub">Where docs can be published. Publish each doc from its editor.</p>
          </div>
        </div>
        <ul className="dest-strip-list">
          <li>
            <Icon name="globe" size={22} />
            <div>
              <div className="dest-name">Hosted Sites</div>
              {hostedSites.length > 0
                ? <StatusBadge tone="success">{activeSitesCount} active</StatusBadge>
                : <StatusBadge tone="neutral">No sites yet</StatusBadge>}
            </div>
            <button type="button" className="link" onClick={hostedSites.length ? goToSites : addSite}>
              {hostedSites.length ? 'Manage' : 'Create a site'}
            </button>
          </li>
          {/* Help-desk and wiki targets are not relevant for the design persona. */}
          {!isDesignPersona && (
            <>
              <li>
                <Icon name="headset" size={22} />
                <div>
                  <div className="dest-name">Freshdesk</div>
                  {freshdeskConnected
                    ? <StatusBadge tone="success">Connected</StatusBadge>
                    : <StatusBadge tone="neutral">Not connected</StatusBadge>}
                </div>
                <button type="button" className="link" onClick={() => goToConnections('helpdesk')}>
                  {freshdeskConnected ? 'Manage' : 'Connect'}
                </button>
              </li>
              <li>
                <Icon name="book" size={22} />
                <div>
                  <div className="dest-name">Confluence</div>
                  {atlassianConnected
                    ? <StatusBadge tone="success">Connected</StatusBadge>
                    : <StatusBadge tone="neutral">Not connected</StatusBadge>}
                </div>
                <button type="button" className="link" onClick={() => goToConnections('wiki')}>
                  {atlassianConnected ? 'Manage' : 'Connect'}
                </button>
              </li>
            </>
          )}
        </ul>
      </section>

      {loading ? (
        <SkeletonRows rows={4} label="Loading documents" />
      ) : articlesError && list.length === 0 ? (
        <StatePanel
          kind="error"
          title="Couldn’t load your documents"
          action={<button type="button" className="btn btn-secondary" onClick={() => refetchArticles()}><Icon name="refresh" /> Retry</button>}
        >
          Check your connection and try again.
        </StatePanel>
      ) : list.length === 0 ? (
        <StatePanel
          title="No documents yet"
          action={
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button type="button" className="btn btn-primary" onClick={onNew}><Icon name="plus" /> New document</button>
              <button type="button" className="btn btn-secondary" onClick={openFetchModal}>Fetch from a destination</button>
            </div>
          }
        >
          Create one from a recording, or import existing articles from a connected destination.
        </StatePanel>
      ) : (
        <section aria-label="Documents" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pub-toolbar">
            <div className="tablist" role="tablist" aria-label="Filter documents by state">
              {tabs.map((t, i) => (
                <button
                  key={t.id}
                  id={`pub-tab-${t.id}`}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === t.id}
                  aria-controls="pub-docs"
                  tabIndex={statusFilter === t.id ? 0 : -1}
                  onClick={() => setStatusFilter(t.id)}
                  onKeyDown={e => onTabKey(e, i)}
                >
                  {t.label} <span className="tab-count">{t.count}</span>
                </button>
              ))}
            </div>
            <div className="pub-toolbar-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => (selectMode ? clearSelection() : setSelectMode(true))} aria-pressed={selectMode}>
                {selectMode ? 'Cancel selection' : 'Select'}
              </button>
              {hasFreshdeskDocs && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={republishing} aria-busy={republishing || undefined} onClick={handleRepublishAll}
                  title="Re-push all Freshdesk-synced docs to update their content and GIFs in place">
                  {republishing ? <><Spinner size={14} /> Republishing {republish.done}/{republish.total}</> : <><Icon name="refresh" /> Republish all</>}
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-sm" onClick={openFetchModal} title="Import docs from Freshdesk or Confluence">
                <Icon name="upload" style={{ transform: 'rotate(180deg)' }} /> Fetch
              </button>
            </div>
          </div>

          {selectMode && (
            <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px' }} role="region" aria-label="Bulk actions">
              <span style={{ fontWeight: 500 }} aria-live="polite">{selectedIds.size} selected</span>
              <span style={{ flex: 1 }} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection}>Clear</button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={selectedIds.size === 0 || bulkDeleting}
                aria-busy={bulkDeleting || undefined}
                onClick={handleBulkDelete}
              >
                {bulkDeleting ? <><Spinner size={14} /> Deleting…</> : 'Delete selected'}
              </button>
            </div>
          )}

          {articlesError && (
            <div className="stale-note" role="status">
              <Icon name="warning" size={18} />
              Couldn’t refresh — showing the last loaded list.
              <button type="button" className="link" onClick={() => refetchArticles()} disabled={articlesFetching}>Retry</button>
            </div>
          )}

          <div id="pub-docs" role="tabpanel" aria-labelledby={`pub-tab-${statusFilter}`}>
            {rows.length === 0 ? (
              <StatePanel icon="search" title="No documents match" compact
                action={(q || statusFilter !== 'all') && (
                  <button type="button" className="btn btn-secondary" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</button>
                )}>
                {q ? <>Nothing matches “{search.trim()}” in this view.</> : 'No documents are in this state yet.'}
              </StatePanel>
            ) : isNarrow ? (
              <ul className="card-list">
                {rows.map(r => <ArticleCard {...rowProps(r)} />)}
              </ul>
            ) : (
              <div className="table-scroll" role="region" aria-label="Documents" tabIndex={0}>
                <table className="data-table" style={{ minWidth: 760 }}>
                  <thead>
                    <tr>
                      {selectMode && <th scope="col" style={{ width: 48 }}><span className="visually-hidden">Select</span></th>}
                      <th scope="col">Title</th>
                      <th scope="col">Type</th>
                      <th scope="col">State</th>
                      <th scope="col">Destination</th>
                      <th scope="col">Updated</th>
                      <th scope="col" className="cell-actions"><span className="visually-hidden">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => <ArticleRow {...rowProps(r)} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Fetch dialog */}
      <Dialog
        open={showFetchModal}
        onClose={() => setShowFetchModal(false)}
        title="Fetch docs"
        description="Import existing articles from a connected destination into Flow for editing."
        maxWidth={560}
        footer={<button type="button" className="btn btn-secondary" onClick={() => setShowFetchModal(false)}>Close</button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {fetchError && (
            <div className="inline-msg is-error" role="alert"><Icon name="alert" /> {fetchError}</div>
          )}
          {fetchResult && (
            <div className="inline-msg is-success" role="status">
              <Icon name="checkCircle" /> Fetched from {fetchResult.source} — {fetchResult.imported} imported, {fetchResult.skipped} already present
            </div>
          )}

          <div className="fetch-source">
            <div className="fetch-source-head">
              <Icon name="headset" />
              <div style={{ flex: 1 }}>
                <div className="dest-name">Freshdesk</div>
                <div className="meta">Knowledge base articles</div>
              </div>
              {!integrationStatus.freshdesk && <StatusBadge tone="neutral">Not connected</StatusBadge>}
            </div>
            {integrationStatus.freshdesk ? (
              <button type="button" className="btn btn-secondary" onClick={handleImportFreshdesk} disabled={fetchingFD} aria-busy={fetchingFD || undefined}>
                {fetchingFD ? <><Spinner size={14} /> Importing…</> : 'Import from Freshdesk'}
              </button>
            ) : (
              <p className="meta">
                Connect Freshdesk in <Link className="link" to="/settings?category=helpdesk#connections">Settings → Connections</Link> to import articles.
              </p>
            )}
          </div>

          <div className="fetch-source">
            <div className="fetch-source-head">
              <Icon name="book" />
              <div style={{ flex: 1 }}>
                <div className="dest-name">Confluence</div>
                <div className="meta">Wiki pages</div>
              </div>
              {!integrationStatus.atlassian && <StatusBadge tone="neutral">Not connected</StatusBadge>}
            </div>
            {integrationStatus.atlassian ? (
              loadingConfSpaces ? (
                <div className="meta" role="status"><Spinner size={14} /> Loading spaces…</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <label className="label" htmlFor="fetch-space">Space</label>
                    <select id="fetch-space" className="select" value={selectedSpace} onChange={e => setSelectedSpace(e.target.value)}>
                      {confSpaces.map(sp => <option key={sp.key} value={sp.key}>{sp.name}</option>)}
                    </select>
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={handleImportConfluence} disabled={fetchingConf || !selectedSpace} aria-busy={fetchingConf || undefined}>
                    {fetchingConf ? <><Spinner size={14} /> Importing…</> : 'Import'}
                  </button>
                </div>
              )
            ) : (
              <p className="meta">
                Connect Confluence in <Link className="link" to="/settings?category=wiki#connections">Settings → Connections</Link> to import pages.
              </p>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ── Row pieces shared by the table row and the mobile card ──────────────────

function useRowState(article, allArticles) {
  const [confirming, setConfirming] = useState(false);
  const [parentPicker, setParentPicker] = useState(false);
  // Candidate parents: any other top-level doc (avoid self; avoid nesting under an existing child)
  const parentCandidates = allArticles.filter(x => x.id !== article.id && !x.parent_id);
  const publishedTo = DESTINATIONS.filter(d => article[d.idField]);
  const status = STATUS[article.status] || { label: article.status || '—', tone: 'neutral' };
  const isReview = article.use_case === 'design_review';
  return { confirming, setConfirming, parentPicker, setParentPicker, parentCandidates, publishedTo, status, isReview };
}

function TitleCell({ article, depth, parentTitle }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingLeft: depth ? 28 : 0, minWidth: 0 }}>
      {depth > 0 ? (
        <span className="meta" style={{ lineHeight: '20px' }} title={parentTitle ? `Nested under “${parentTitle}” — publishes as a sub-article.` : 'Nested sub-article'}>
          <span aria-hidden="true">↳</span><span className="visually-hidden">Sub-article of {parentTitle || 'another doc'}:</span>
        </span>
      ) : (
        <Icon name={article.use_case === 'design_review' ? 'review' : 'file'} size={20} style={{ color: 'var(--gp-muted)', flexShrink: 0 }} />
      )}
      <div style={{ minWidth: 0 }}>
        <div className="cell-title">{article.title || 'Untitled'}</div>
        {Array.isArray(article.documents_tickets) && article.documents_tickets.length > 0 && (
          <div className="meta" style={{ marginTop: 2 }}>Documents {article.documents_tickets.join(', ')}</div>
        )}
      </div>
    </div>
  );
}

function DestinationText({ publishedTo }) {
  return publishedTo.length
    ? <span>{publishedTo.map(d => d.label).join(', ')}</span>
    : <span className="cell-muted">Not published</span>;
}

function RowMenu({ rs, article, depth, onUpdate, onChangeParent }) {
  return (
    <MenuButton label={`More actions for ${article.title || 'this doc'}`}>
      {({ close }) => !rs.parentPicker ? (
        <>
          <button type="button" className="menu-item" onClick={() => { close(); onUpdate(); }}>
            <Icon name="video" /> New version from a recording
          </button>
          <button type="button" className="menu-item" onClick={() => rs.setParentPicker(true)}>
            <Icon name="chevronRight" /> {depth > 0 ? 'Change parent…' : 'Nest under another doc…'}
          </button>
          {depth > 0 && (
            <button type="button" className="menu-item" onClick={() => { onChangeParent(null); close(); }}>
              <Icon name="chevronLeft" /> Make top-level
            </button>
          )}
          <div className="menu-sep" role="separator" />
          <button type="button" className="menu-item is-danger" onClick={() => { close(); rs.setConfirming(true); }}>
            <Icon name="trash" /> Delete…
          </button>
        </>
      ) : (
        <div style={{ padding: 6 }}>
          <label className="label" htmlFor={`parent-${article.id}`}>Nest this doc under</label>
          {rs.parentCandidates.length === 0 ? (
            <p className="meta">No eligible parent docs.</p>
          ) : (
            <select
              id={`parent-${article.id}`}
              className="select"
              defaultValue=""
              onChange={(e) => {
                const pid = e.target.value ? parseInt(e.target.value, 10) : null;
                if (pid) { onChangeParent(pid); rs.setParentPicker(false); close(); }
              }}
            >
              <option value="">Select a parent…</option>
              {rs.parentCandidates.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          )}
        </div>
      )}
    </MenuButton>
  );
}

function DeleteConfirm({ rs, article, onDelete }) {
  return (
    <div className="row-confirm" role="group" aria-label={`Confirm delete ${article.title || ''}`}>
      <span>
        Delete this doc?
        {rs.publishedTo.length > 0 && <> Also deletes it from {rs.publishedTo.map(d => d.label).join(' and ')}.</>}
      </span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => rs.setConfirming(false)} autoFocus>Cancel</button>
      <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>Delete</button>
    </div>
  );
}

function ArticleRow({ article, depth, onEdit, onUpdate, onPublish, onDelete, deleteError, parentTitle, selectMode, selected, onToggleSelect, allArticles = [], onChangeParent }) {
  const rs = useRowState(article, allArticles);
  const colCount = selectMode ? 7 : 6;
  return (
    <>
      <tr className={selected ? 'is-selected' : undefined}>
        {selectMode && (
          <td>
            <input type="checkbox" checked={!!selected} onChange={onToggleSelect} style={{ width: 18, height: 18, accentColor: 'var(--gp-accent)' }}
              aria-label={`Select ${article.title || 'doc'}`} />
          </td>
        )}
        <td><TitleCell article={article} depth={depth} parentTitle={parentTitle} /></td>
        <td className="cell-muted">{rs.isReview ? 'Review' : 'Document'}</td>
        <td><StatusBadge tone={rs.status.tone}>{rs.status.label}</StatusBadge></td>
        <td><DestinationText publishedTo={rs.publishedTo} /></td>
        <td className="cell-muted cell-nowrap">{formatDate(article.updated_at || article.created_at)}</td>
        <td className="cell-actions">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>Open<span className="visually-hidden"> {article.title}</span></button>
            {onPublish && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onPublish}
                title="Open the editor’s publish flow to choose destinations">
                Publish<span className="visually-hidden"> {article.title}</span>
              </button>
            )}
            <RowMenu rs={rs} article={article} depth={depth} onUpdate={onUpdate} onChangeParent={onChangeParent} />
          </div>
        </td>
      </tr>
      {(rs.confirming || deleteError) && (
        <tr className="row-sub">
          <td colSpan={colCount}>
            {rs.confirming && <DeleteConfirm rs={rs} article={article} onDelete={onDelete} />}
            {deleteError && <div className="inline-msg is-warning" role="alert"><Icon name="warning" /> Deleted in Flow, but removing it remotely failed: {deleteError}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function ArticleCard({ article, depth, onEdit, onUpdate, onPublish, onDelete, deleteError, parentTitle, selectMode, selected, onToggleSelect, allArticles = [], onChangeParent }) {
  const rs = useRowState(article, allArticles);
  return (
    <li style={selected ? { borderColor: 'var(--gp-accent)', background: 'var(--gp-accent-dim)' } : depth ? { marginLeft: 16 } : undefined}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {selectMode && (
          <input type="checkbox" checked={!!selected} onChange={onToggleSelect} style={{ width: 20, height: 20, marginTop: 2, accentColor: 'var(--gp-accent)' }}
            aria-label={`Select ${article.title || 'doc'}`} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <TitleCell article={article} depth={0} parentTitle={parentTitle} />
          {depth > 0 && <div className="meta" style={{ marginTop: 2 }}>Sub-article of {parentTitle || 'another doc'}</div>}
        </div>
        <RowMenu rs={rs} article={article} depth={depth} onUpdate={onUpdate} onChangeParent={onChangeParent} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusBadge tone={rs.status.tone}>{rs.status.label}</StatusBadge>
        <span className="meta">{rs.isReview ? 'Review' : 'Document'} · <DestinationText publishedTo={rs.publishedTo} /> · {formatDate(article.updated_at || article.created_at)}</span>
      </div>
      {rs.confirming && <DeleteConfirm rs={rs} article={article} onDelete={onDelete} />}
      {deleteError && <div className="inline-msg is-warning" role="alert"><Icon name="warning" /> Remote delete failed: {deleteError}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onEdit}>Open</button>
        {onPublish && <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onPublish}>Publish</button>}
      </div>
    </li>
  );
}
